import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import DatePicker from '../../components/DatePicker'
import FileField from '../../components/FileField'
import FormPage from '../../components/FormPage'
import { FormCols, FormSection, FormWell, SwitchField } from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import useGoBack from '../../components/useGoBack'
import { useToast } from '../../components/toastContext'
import { getPoliciesPage, listPolicies, savePolicy } from './engagementService'
import { policiesStrings as S } from './strings'
import { serverText } from '../../lib/i18n'

const EMPTY = {
  name: '',
  nameEn: '',
  version: '1',
  readOnly: 'false',
  categoryId: '',
  fileAr: null,
  fileEn: null,
  forceUsers: false,
  evaluationStartDate: '',
  groups: [],
}

/**
 * سياسة جديدة / تعديل — the policy form, on its own page.
 *
 * It was a side sheet, and it is the wrong shape for what this form holds: two names,
 * a version, two kinds of PDF, an enforcement date, and a department picker over a
 * directory that can run to thousands. The picker alone wants more room than a sheet
 * has, and it was the control people came here for.
 *
 * Three decisions, three steps: what the policy IS, the documents themselves, and who
 * has to read it.
 *
 * `نوع السياسة` is the legacy's own odd pairing: a two-option select whose value is
 * the `ReadOnly` flag, so "للموافقة" (false) means the policy has to be acknowledged
 * and "للإطلاع" (true) means it is only read. It stays a select because those two are
 * a CHOICE of kind, not a toggle of one property.
 *
 * The enforcement date only exists while enforcement is on — the legacy shows the
 * field regardless, so an admin can set a date that will never apply.
 */
export default function PolicyFormPage() {
  const { id } = useParams()
  const location = useLocation()
  const goBack = useGoBack('/policies')
  const toast = useToast()

  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [categories, setCategories] = useState([])
  const [groups, setGroups] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true

    getPoliciesPage().then((page) => {
      if (!alive || !page) return
      setCategories(
        (page.categories ?? []).map((category) => ({ value: category.id, label: category.name })),
      )
      setGroups(page.groups ?? [])
    })

    if (!id) return () => {
      alive = false
    }

    /* The row travels with the click, so editing opens filled in with no second
       request; a form reached by its own URL reads the list for its policy. */
    const carried = location.state?.policy
    const seed = (policy) => {
      if (!alive || !policy) return
      setValues({
        ...EMPTY,
        ...policy,
        version: String(policy.version ?? 1),
        readOnly: String(Boolean(policy.readOnly)),
        evaluationStartDate: policy.evaluationStartDate ?? '',
        /* The files are the stored ones — a save without new uploads keeps them. */
        fileAr: null,
        fileEn: null,
      })
    }

    if (carried) seed(carried)
    else {
      listPolicies()
        .then((list) => seed(list.rows.find((row) => String(row.id) === String(id))))
        .catch(() => {})
    }

    return () => {
      alive = false
    }
    // The carried row is read once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const steps = [
    { id: 'policy', label: S.form.identity },
    { id: 'files', label: S.form.documents },
    { id: 'audience', label: S.form.audience },
  ]

  /** What each step will not let you leave without. */
  const check = (step) => {
    const found = {}
    if (step === 0) {
      if (!values.name.trim()) found.name = S.form.required
      if (!values.nameEn.trim()) found.nameEn = S.form.required
      if (!String(values.version).trim()) found.version = S.form.required
    }
    if (step === 1) {
      /* A new policy needs both documents; an existing one keeps the stored pair
         unless a new file is chosen. */
      if (!id && !values.fileAr) found.fileAr = S.form.required
      if (!id && !values.fileEn) found.fileEn = S.form.required
    }
    if (step === 2 && values.groups.length === 0) {
      /* The API refuses a policy with no audience, so the form asks before it posts. */
      found.groups = S.form.required
    }
    return found
  }

  const validateStep = (step) => {
    const found = check(step)
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const submit = async (event) => {
    event.preventDefault()
    const found = { ...check(0), ...check(1), ...check(2) }
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setSaving(true)
    const outcome = await savePolicy({
      ...values,
      id: id ? Number(id) : 0,
      version: Number(values.version),
      readOnly: values.readOnly === 'true',
    })
    setSaving(false)

    if (!outcome.ok) {
      toast.error(outcome.message || S.actionFailed)
      return
    }
    toast.success(serverText(outcome.message) || S.saved)
    goBack()
  }

  return (
    <FormPage
      icon={ShieldCheck}
      title={id ? S.row.edit : S.create}
      subtitle={S.title}
      steps={steps}
      validateStep={validateStep}
      onBack={goBack}
      onSubmit={submit}
      submitLabel={id ? S.form.save : S.form.add}
      submitting={saving}
    >
      {({ step }) => {
        if (step === 0) {
          return (
            <FormSection>
              <FormCols>
                <FormRow label={S.form.nameAr} error={errors.name}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      value={values.name}
                      onChange={set('name')}
                      invalid={!!errors.name}
                      autoComplete="off"
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.nameEn} error={errors.nameEn}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      dir="ltr"
                      value={values.nameEn}
                      onChange={set('nameEn')}
                      invalid={!!errors.nameEn}
                      autoComplete="off"
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.version} error={errors.version}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      type="number"
                      min="1"
                      dir="ltr"
                      value={values.version}
                      onChange={set('version')}
                      invalid={!!errors.version}
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.kind}>
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      value={values.readOnly}
                      onChange={set('readOnly')}
                      options={[
                        { value: 'false', label: S.form.acknowledgement },
                        { value: 'true', label: S.form.readOnly },
                      ]}
                    />
                  )}
                </FormRow>

                <FormRow label={S.form.category}>
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      value={values.categoryId}
                      onChange={set('categoryId')}
                      options={categories}
                      placeholder={S.form.categoryPlaceholder}
                    />
                  )}
                </FormRow>
              </FormCols>
            </FormSection>
          )
        }

        if (step === 1) {
          return (
            <FormSection>
              <FormCols>
                <FormRow label={S.form.fileAr} error={errors.fileAr} hint={S.form.fileTypes}>
                  {(fieldId) => <FileField id={fieldId} accept=".pdf" onChange={set('fileAr')} />}
                </FormRow>

                <FormRow label={S.form.fileEn} error={errors.fileEn} hint={S.form.fileTypes}>
                  {(fieldId) => <FileField id={fieldId} accept=".pdf" onChange={set('fileEn')} />}
                </FormRow>
              </FormCols>
            </FormSection>
          )
        }

        return (
          <>
            <SwitchField
              checked={values.forceUsers}
              onChange={set('forceUsers')}
              label={S.form.forceUsers}
              hint={S.form.forceUsersHint}
            />

            {/* The date only means anything while enforcement is on. The legacy
                shows it either way, so a date can be set that never applies. */}
            {values.forceUsers && (
              <FormWell>
                <FormRow label={S.form.evaluationStartDate} className="form-narrow">
                  {(fieldId) => (
                    <DatePicker
                      id={fieldId}
                      value={values.evaluationStartDate}
                      onChange={set('evaluationStartDate')}
                    />
                  )}
                </FormRow>
              </FormWell>
            )}

            {/* WHO THE POLICY IS FOR. The legacy form has this and the clone did not; the
                API refuses a policy that reaches nobody, so it is not optional. */}
            <FormSection>
              <GroupPicker
                options={groups}
                selected={values.groups}
                onChange={set('groups')}
                legend={S.form.groups}
                error={errors.groups}
                selectAllLabel={S.form.selectAll}
                filterLabel={S.form.filter}
              />
            </FormSection>
          </>
        )
      }}
    </FormPage>
  )
}
